import React, { useState, useEffect } from "react";
import {
  MoonIcon,
  SunIcon,
  BarChartIcon,
  SettingsIcon,
  WalletIcon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

// Interface for Pool Strategy
interface PoolStrategy {
  id: string;
  name: string;
  initialInvestment: number;
  apy: number;
  riskScore: number;
  liquidityScore: number;
}

const YieldFarmingCalculator: React.FC = () => {
  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Active menu item
  const [activeMenu, setActiveMenu] = useState("calculator");

  // State for pool strategies
  const [strategies, setStrategies] = useState<PoolStrategy[]>([
    {
      id: "stable-1",
      name: "Stable Coin Pool",
      initialInvestment: 1000,
      apy: 5.5,
      riskScore: 2,
      liquidityScore: 9,
    },
    {
      id: "bluechip-1",
      name: "Blue Chip DeFi",
      initialInvestment: 1000,
      apy: 8.2,
      riskScore: 4,
      liquidityScore: 7,
    },
  ]);

  // State for new strategy dialog
  const [newStrategy, setNewStrategy] = useState<Omit<PoolStrategy, "id">>({
    name: "",
    initialInvestment: 1000,
    apy: 5.0,
    riskScore: 3,
    liquidityScore: 5,
  });

  // State for simulation parameters
  const [simulationYears, setSimulationYears] = useState<number>(3);
  const [compoundFrequency, setCompoundFrequency] = useState<number>(12);

  // Effect to handle theme changes
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Rest of the previous implementation remains the same...
  // (calculateProjectedReturns, handleAddStrategy, removeStrategy, etc.)
  // Calculate projected returns
  const calculateProjectedReturns = (strategy: PoolStrategy) => {
    const projectedData = [];
    let currentValue = strategy.initialInvestment;

    for (let year = 0; year <= simulationYears; year++) {
      projectedData.push({
        year,
        value: currentValue,
        apy: strategy.apy,
      });

      // Calculate compound interest
      const periodicRate = strategy.apy / 100 / compoundFrequency;
      currentValue *= Math.pow(1 + periodicRate, compoundFrequency);
    }

    return projectedData;
  };

  // Add new strategy
  const handleAddStrategy = () => {
    const newStrategyWithId = {
      ...newStrategy,
      id: `strategy-${Date.now()}`,
    };
    setStrategies([...strategies, newStrategyWithId]);
    // Reset new strategy form
    setNewStrategy({
      name: "",
      initialInvestment: 1000,
      apy: 5.0,
      riskScore: 3,
      liquidityScore: 5,
    });
  };

  // Remove strategy
  const removeStrategy = (id: string) => {
    setStrategies(strategies.filter((strategy) => strategy.id !== id));
  };

  // Render calculator content
  const renderCalculator = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block mb-2">Simulation Years</label>
          <Input
            type="number"
            value={simulationYears}
            onChange={(e) => setSimulationYears(Number(e.target.value))}
            min={1}
            max={10}
          />
        </div>
        <div>
          <label className="block mb-2">Compound Frequency (per year)</label>
          <Input
            type="number"
            value={compoundFrequency}
            onChange={(e) => setCompoundFrequency(Number(e.target.value))}
            min={1}
            max={365}
          />
        </div>
        <div className="flex items-end">
          <Dialog>
            <DialogTrigger asChild>
              <Button className="w-full">
                <PlusIcon className="mr-2 h-4 w-4" /> Add Strategy
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Strategy</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="block mb-2">Strategy Name</label>
                  <Input
                    value={newStrategy.name}
                    onChange={(e) =>
                      setNewStrategy({
                        ...newStrategy,
                        name: e.target.value,
                      })
                    }
                    placeholder="Enter strategy name"
                  />
                </div>
                <div>
                  <label className="block mb-2">Initial Investment ($)</label>
                  <Input
                    type="number"
                    value={newStrategy.initialInvestment}
                    onChange={(e) =>
                      setNewStrategy({
                        ...newStrategy,
                        initialInvestment: Number(e.target.value),
                      })
                    }
                    min={0}
                  />
                </div>
                <div>
                  <label className="block mb-2">APY (%)</label>
                  <Input
                    type="number"
                    value={newStrategy.apy}
                    onChange={(e) =>
                      setNewStrategy({
                        ...newStrategy,
                        apy: Number(e.target.value),
                      })
                    }
                    min={0}
                    step={0.1}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2">Risk Score (1-10)</label>
                    <Input
                      type="number"
                      value={newStrategy.riskScore}
                      onChange={(e) =>
                        setNewStrategy({
                          ...newStrategy,
                          riskScore: Number(e.target.value),
                        })
                      }
                      min={1}
                      max={10}
                    />
                  </div>
                  <div>
                    <label className="block mb-2">Liquidity Score (1-10)</label>
                    <Input
                      type="number"
                      value={newStrategy.liquidityScore}
                      onChange={(e) =>
                        setNewStrategy({
                          ...newStrategy,
                          liquidityScore: Number(e.target.value),
                        })
                      }
                      min={1}
                      max={10}
                    />
                  </div>
                </div>
                <Button onClick={handleAddStrategy} className="w-full">
                  Create Strategy
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <LineChart>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="year"
            label={{ value: "Years", position: "insideBottom", offset: -5 }}
          />
          <YAxis
            label={{
              value: "Portfolio Value ($)",
              angle: -90,
              position: "insideLeft",
            }}
          />
          <Tooltip />
          <Legend />
          {strategies.map((strategy, index) => (
            <Line
              key={strategy.id}
              type="monotone"
              data={calculateProjectedReturns(strategy)}
              dataKey="value"
              name={strategy.name}
              stroke={`hsl(${index * 60}, 70%, 50%)`}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {strategies.map((strategy) => {
          const projectedData = calculateProjectedReturns(strategy);
          const finalValue = projectedData[projectedData.length - 1].value;
          const totalReturn =
            ((finalValue - strategy.initialInvestment) /
              strategy.initialInvestment) *
            100;

          return (
            <Card key={strategy.id} className="p-4 relative">
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                onClick={() => removeStrategy(strategy.id)}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
              <h3 className="text-lg font-bold mb-2">
                Strategy Summary: {strategy.name}
              </h3>
              <div className="space-y-1">
                <p>
                  Initial Investment: ${strategy.initialInvestment.toFixed(2)}
                </p>
                <p>Final Portfolio Value: ${finalValue.toFixed(2)}</p>
                <p>Total Return: {totalReturn.toFixed(2)}%</p>
                <p>Risk Score: {strategy.riskScore}/10</p>
                <p>Liquidity Score: {strategy.liquidityScore}/10</p>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );

  // Render wallet section
  const renderWallet = () => (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Your Strategies</h2>
      {strategies.length === 0 ? (
        <p className="text-center text-muted-foreground">
          No strategies added yet. Use the calculator to create your first
          strategy.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {strategies.map((strategy) => (
            <Card key={strategy.id} className="p-4">
              <h3 className="text-lg font-bold mb-2">{strategy.name}</h3>
              <div className="space-y-1">
                <p>Investment: ${strategy.initialInvestment.toFixed(2)}</p>
                <p>APY: {strategy.apy.toFixed(2)}%</p>
                <p>Risk Score: {strategy.riskScore}/10</p>
                <Button
                  variant="destructive"
                  className="w-full mt-2"
                  onClick={() => removeStrategy(strategy.id)}
                >
                  <TrashIcon className="mr-2 h-4 w-4" /> Remove
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render settings content
  const renderSettings = () => (
    <div className="p-4 space-y-4">
      <h2 className="text-2xl font-bold">Settings</h2>
      <div className="flex items-center justify-between">
        <span>Dark Mode</span>
        <div className="flex items-center space-x-2">
          <SunIcon className="h-4 w-4" />
          <Switch checked={isDarkMode} onCheckedChange={toggleTheme} />
          <MoonIcon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );

  // Main render method remains the same as in the previous implementation
  return (
    <div className="min-h-screen flex flex-col">
      {/* Main Content Area */}
      <Card className="flex-grow w-full max-w-6xl mx-auto mb-16">
        <CardHeader>
          <CardTitle>Yield Farming Optimization Calculator</CardTitle>
        </CardHeader>
        <CardContent>
          {activeMenu === "calculator" && renderCalculator()}
          {activeMenu === "wallet" && renderWallet()}
          {activeMenu === "settings" && renderSettings()}
        </CardContent>
      </Card>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-background border-t dark:border-gray-700 z-50">
        <div className="grid grid-cols-3 gap-2 p-2">
          <button
            onClick={() => setActiveMenu("calculator")}
            className={`flex flex-col items-center justify-center py-2 ${
              activeMenu === "calculator"
                ? "text-primary"
                : "text-muted-foreground"
            }`}
          >
            <BarChartIcon className="h-6 w-6 mb-1" />
            <span className="text-xs">Calculator</span>
          </button>
          <button
            onClick={() => setActiveMenu("wallet")}
            className={`flex flex-col items-center justify-center py-2 ${
              activeMenu === "wallet" ? "text-primary" : "text-muted-foreground"
            }`}
          >
            <WalletIcon className="h-6 w-6 mb-1" />
            <span className="text-xs">Strategies</span>
          </button>
          <button
            onClick={() => setActiveMenu("settings")}
            className={`flex flex-col items-center justify-center py-2 ${
              activeMenu === "settings"
                ? "text-primary"
                : "text-muted-foreground"
            }`}
          >
            <SettingsIcon className="h-6 w-6 mb-1" />
            <span className="text-xs">Settings</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default YieldFarmingCalculator;
